/** Client-side CSV parsing and column mapping for contact imports. */

export const IMPORT_TARGETS = [
  { value: "ignore", label: "Don't import" },
  { value: "email", label: "Email" },
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "name", label: "Full name" },
  { value: "segments", label: "Segment" },
  { value: "tags", label: "Tags" },
  { value: "title", label: "Title / article title" },
  { value: "subject_line", label: "Subject line (per contact)" },
  { value: "message", label: "Message (per contact)" },
];

const EMAIL_ALIASES = new Set(["email", "e_mail", "e-mail", "email_address", "mail", "emailaddress"]);
const FIRST_NAME_ALIASES = new Set(["first_name", "firstname", "first", "fname", "given_name", "givenname"]);
const LAST_NAME_ALIASES = new Set(["last_name", "lastname", "last", "lname", "surname", "family_name", "familyname"]);
const NAME_ALIASES = new Set(["name", "full_name", "fullname", "contact_name", "display_name", "contact"]);
const TAGS_ALIASES = new Set(["tags", "tag", "labels", "label"]);
const SEGMENTS_ALIASES = new Set(["segments", "segment", "groups", "group"]);
const TITLE_ALIASES = new Set(["title", "article_title", "article", "article_name", "paper_title", "publication_title"]);
const SUBJECT_LINE_ALIASES = new Set(["subject_line", "subject", "email_subject", "mail_subject"]);
const MESSAGE_ALIASES = new Set(["message", "message_body", "body", "email_body", "content", "article_body"]);

export function normalizeHeader(header) {
  return (header || "").trim().toLowerCase().replace(/[\s-]+/g, "_").replace(/^_|_$/g, "");
}

function aliasTarget(normalized) {
  if (EMAIL_ALIASES.has(normalized)) return "email";
  if (FIRST_NAME_ALIASES.has(normalized)) return "first_name";
  if (LAST_NAME_ALIASES.has(normalized)) return "last_name";
  if (NAME_ALIASES.has(normalized)) return "name";
  if (SEGMENTS_ALIASES.has(normalized)) return "segments";
  if (TAGS_ALIASES.has(normalized)) return "tags";
  if (TITLE_ALIASES.has(normalized)) return "title";
  if (SUBJECT_LINE_ALIASES.has(normalized)) return "subject_line";
  if (MESSAGE_ALIASES.has(normalized)) return "message";
  return null;
}

export function autoMapColumns(headers) {
  const mapping = {};
  const used = new Set();
  for (const header of headers) {
    const target = aliasTarget(normalizeHeader(header));
    if (target && !used.has(target)) {
      mapping[header] = target;
      used.add(target);
    }
  }
  return mapping;
}

function splitFullName(name) {
  const parts = (name || "").trim().split(/\s+/);
  if (!parts.length || !parts[0]) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

function parseTagsValue(value) {
  if (value == null || value === "") return [];
  const s = String(value).trim();
  if (!s) return [];
  const sep = s.includes("|") ? "|" : ",";
  return s.split(sep).map((t) => t.trim()).filter(Boolean);
}

function firstSegmentValue(value) {
  const parts = parseTagsValue(value);
  return parts[0] || "";
}

export function applyMapping(row, mapping) {
  const out = {
    email: "",
    first_name: "",
    last_name: "",
    tags: [],
    segment: "",
    title: "",
    subject_line: "",
    message: "",
  };
  const tagParts = [];
  for (const [csvCol, target] of Object.entries(mapping)) {
    if (target === "ignore") continue;
    const text = String(row[csvCol] ?? "").trim();
    if (!text) continue;
    if (target === "email") out.email = text.toLowerCase();
    else if (target === "first_name") out.first_name = text;
    else if (target === "last_name") out.last_name = text;
    else if (target === "name") {
      const { first_name, last_name } = splitFullName(text);
      if (!out.first_name) out.first_name = first_name;
      if (!out.last_name) out.last_name = last_name;
    } else if (target === "tags") {
      tagParts.push(...parseTagsValue(text));
    } else if (target === "segments") {
      if (!out.segment) out.segment = firstSegmentValue(text);
    } else if (target === "title") out.title = text;
    else if (target === "subject_line") out.subject_line = text;
    else if (target === "message") out.message = text;
  }
  if (tagParts.length) out.tags = [...new Set(tagParts)];
  return out;
}

/** Minimal RFC 4180 CSV row parser. */
function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out.map((v) => v.trim());
}

export function parseCsvText(text) {
  const raw = (text || "").replace(/^\uFEFF/, "");
  const lines = raw.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };

  const first = parseCsvLine(lines[0]);
  const hasHeader = first.some((cell) => aliasTarget(normalizeHeader(cell)));
  if (!hasHeader && first.some((cell) => cell.includes("@"))) {
    const rows = lines.map((line) => {
      const cells = parseCsvLine(line);
      return { email: (cells[0] || "").trim() };
    });
    return { headers: ["email"], rows };
  }

  const headers = first;
  const rows = lines.slice(1).map((line) => {
    const cells = parseCsvLine(line);
    const row = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] ?? "";
    });
    return row;
  }).filter((row) => Object.values(row).some((v) => String(v).trim()));
  return { headers, rows };
}

export async function parseCsvFile(file) {
  const text = await file.text();
  return parseCsvText(text);
}

export function buildImportPreview(headers, rows, mapping) {
  const effective = { ...mapping };
  for (const h of headers) {
    if (!(h in effective)) effective[h] = "ignore";
  }
  const mapped = rows.map((row) => applyMapping(row, effective));
  const seen = new Set();
  const unique = [];
  let invalidEmails = 0;
  let duplicatesInFile = 0;
  for (const c of mapped) {
    const email = String(c.email || "").trim().toLowerCase();
    if (!email || !email.includes("@")) {
      invalidEmails += 1;
      continue;
    }
    if (seen.has(email)) {
      duplicatesInFile += 1;
      continue;
    }
    seen.add(email);
    unique.push({ ...c, email });
  }
  const unmapped = headers.filter((h) => !(h in mapping));
  const emailMapped = Object.values(mapping).includes("email");
  return {
    mapping: effective,
    contacts: unique,
    unmapped,
    needsMapping: unmapped.length > 0 || !emailMapped,
    sample: unique.slice(0, 5),
    totalRows: mapped.filter((c) => String(c.email || "").trim()).length,
    uniqueEmails: unique.length,
    duplicatesInFile,
    invalidEmails,
    alreadyInAudience: 0,
    willImport: unique.length,
    total: unique.length,
  };
}

export function validateMapping(mapping) {
  const values = Object.values(mapping);
  if (!values.includes("email")) {
    return "Map one column to Email";
  }
  const dupes = values.filter((v) => v !== "ignore");
  const seen = new Set();
  for (const v of dupes) {
    if (seen.has(v)) return `Column "${v}" is mapped more than once`;
    seen.add(v);
  }
  return null;
}
